import type { JSX } from 'react';
import {
    ConfirmSaveHeader,
    HalfSlideIn,
    LoaderHeader,
    SlideInBody,
    SlideInHeaderTexts,
    StandardFirstLevelHeader,
} from '../../ui/slidein/Slidein.tsx';
import { LabeledText } from '../../ui/labeled-elements/LabeledText.tsx';
import { usbStickTexts } from '../i18n/UsbStickTexts.ts';
import * as UsbStickState from '../state/UsbStickState.ts';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { getOrElse, isDefined, Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import { UsbStickDetail } from 'dg-web-shared/model/UsbStick.ts';
import { languageTexts } from '../../common/i18n/GeneralTexts.ts';
import { UsbStickSyncStatus } from './UsbStickSyncStatus.tsx';
import { TextButton } from '../../ui/buttons/TextButton.tsx';
import { SlideInHeaderButtonsContainer } from '../../ui/slidein/SlideInHeader2.tsx';
import { openUsbAppOrInfo } from '../../sync-app/actions/SyncAppActions.ts';
import * as SyncAppHelpSlideInState from '../../sync-app/state/SyncAppHelpSlideInState.ts';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import { CustomPermissionCheckers } from '../../app/state/Permission.ts';
import { RestrictedComponent } from '../../app/components/RestrictedComponent.tsx';
import { TextField } from '../../ui/labeled-elements/TextField.tsx';
import { editBaseData, makeEditPayload } from '../actions/UsbStickActions.ts';
import { TomStatusIconPositioning } from '../../tom/components/TomFirmwareStatus.tsx';
import { OperatorAppRoutes } from '../../app/config/OperatorRoutingConfig.tsx';
import { UsbAppAction } from 'taxomex-shared/usbApp/UsbAppAction.ts';

export function UsbStickDetailSlideIn() {
    const { storeState, update } = useStore(store => {
        const layoutState = UsbStickState.Layout.get(store);
        const usbStickId = layoutState.selectedUsbStickId;
        return {
            layout: layoutState,
            usbSticks: UsbStickState.List.get(store),
            syncSlideIn: SyncAppHelpSlideInState.Layout.get(store),
            usbStickDetail: usbStickId
                ? UsbStickState.Detail.get(store, { usbStickId })
                : null,
            edit: UsbStickState.Edit.get(store),
            login: CurrentOperatorLoginState.get(store),
        };
    });
    const { language } = useLanguage();

    function updateStick(usbStick: UsbStickDetail): void {
        update(store =>
            openUsbAppOrInfo(store, {
                usbAppAction: UsbAppAction.UPDATE_STICK,
                usbAppArgs: {
                    operatorId: usbStick.operatorId as number,
                    stickId: usbStick.stickId as number,
                },
                stateResetActionId: 'refetch-after-stick-configuration-write',
                stateResetAction: (store: Store) => {
                    UsbStickState.List.reset(store);
                    UsbStickState.Detail.refetchSameContext(store, false);
                },
            }),
        );
    }

    function closeSlideIn(): void {
        update(UsbStickState.Layout.reset);
        update(UsbStickState.Edit.reset);
    }

    function saveBaseData(usbStick: UsbStickDetail): void {
        update(editBaseData, {
            stickId: usbStick.stickId || 0,
            payload: makeEditPayload(storeState.edit, usbStick),
        });
    }

    const lng = language;
    const txt = usbStickTexts[lng];
    const openSlideIn = isDefined(storeState.layout.selectedUsbStickId);
    if (openSlideIn) {
        const usbStick: Maybe<UsbStickDetail> =
            storeState.usbStickDetail && !storeState.usbStickDetail.pending
                ? storeState.usbStickDetail.data
                : null;
        const login = storeState.login.data;
        if (usbStick && login) {
            const currentConfiguration =
                usbStick.configurations[usbStick.configurations.length - 1];
            const allowEdit = login.permissions.usbStickEdit;
            return (
                <HalfSlideIn open={true} disabled={storeState.syncSlideIn.open}>
                    <SlideInBody>
                        {allowEdit && (
                            <TextField
                                inputType="text"
                                value={getOrElse(
                                    storeState.edit.name,
                                    usbStick.name,
                                )}
                                label={txt.Title()}
                                onChange={(v: string) =>
                                    update(store =>
                                        UsbStickState.Edit.stateWrite(store, {
                                            name: v,
                                        }),
                                    )
                                }
                            />
                        )}
                        {!allowEdit && (
                            <LabeledText label={txt.Title()}>
                                {usbStick.name}
                            </LabeledText>
                        )}
                        <LabeledText label={txt.Id()}>
                            {usbStick.stickId}
                        </LabeledText>
                        <LabeledText label={txt.StickTypeLabel()}>
                            {txt.StickType(usbStick.type)}
                        </LabeledText>
                        <LabeledText label={txt.PinCodeLabel()}>
                            {currentConfiguration.pinCode
                                ? currentConfiguration.pinCode
                                : txt.PinCodeNotSet()}
                        </LabeledText>
                        <LabeledText label={txt.LanguagePlaceholder()}>
                            {languageTexts[currentConfiguration.language]}
                        </LabeledText>
                        <LabeledText label={txt.ExpirationLabel()}>
                            {currentConfiguration.expiryDate
                                ? txt.ExpirationValue(
                                      currentConfiguration.expiryDate,
                                  )
                                : txt.ExpirationNotSet()}
                        </LabeledText>
                        <LabeledText label={txt.Permissions()}>
                            {currentConfiguration.tags
                                .map(t => t.name)
                                .join(', ')}
                        </LabeledText>
                        <LabeledText label={txt.LastWriteLabel()}>
                            {currentConfiguration.lastWrite
                                ? txt.LastWriteValue(
                                      currentConfiguration.lastWrite,
                                  )
                                : txt.LastWriteNever()}
                        </LabeledText>
                        <LabeledText label={txt.SyncStatus()}>
                            <TomStatusIconPositioning
                                label={
                                    <UsbStickSyncStatus
                                        synced={isDefined(
                                            currentConfiguration.lastWrite,
                                        )}
                                    />
                                }
                                text={
                                    currentConfiguration.lastWrite
                                        ? txt.InSync()
                                        : txt.NeedsSynchronization()
                                }
                            />
                        </LabeledText>
                    </SlideInBody>
                    <SlideInHeader
                        language={lng}
                        hasChanges={UsbStickState.Edit.hasChanges(
                            storeState.edit,
                            usbStick,
                        )}
                        stick={usbStick}
                        onUpdateStick={() => updateStick(usbStick)}
                        onClose={() => closeSlideIn()}
                        onSave={() => saveBaseData(usbStick)}
                    />
                </HalfSlideIn>
            );
        } else {
            return (
                <HalfSlideIn open={true}>
                    <LoaderHeader title={txt.SlideInHeaderCaption()} />
                </HalfSlideIn>
            );
        }
    } else {
        return <HalfSlideIn open={false} />;
    }
}

interface UsbSlideInHeaderProps {
    language: string;
    hasChanges: boolean;
    stick: UsbStickDetail;
    onUpdateStick: () => void;
    onClose: () => void;
    onSave: () => void;
}

function SlideInHeader(props: UsbSlideInHeaderProps): JSX.Element {
    const txt = usbStickTexts[props.language];

    if (props.hasChanges) {
        return (
            <ConfirmSaveHeader
                language={props.language}
                title={txt.SaveEdit()}
                onCancel={props.onClose}
                onSave={props.onSave}
            />
        );
    } else {
        return (
            <StandardFirstLevelHeader onClose={props.onClose}>
                <SlideInHeaderTexts
                    title={txt.SlideInHeaderCaption()}
                    hasLeftIcon={false}
                />
                <SlideInHeaderButtonsContainer>
                    <RestrictedComponent
                        route={OperatorAppRoutes.UsbSticks}
                        permissionChecker={
                            CustomPermissionCheckers.syncAppInteractionAllowed
                        }
                    >
                        <TextButton
                            label={txt.WriteToStick()}
                            onClick={props.onUpdateStick}
                        />
                    </RestrictedComponent>
                </SlideInHeaderButtonsContainer>
            </StandardFirstLevelHeader>
        );
    }
}
